// Dados do painel (TanStack Query). Valores da API em centavos; os componentes convertem para reais.
import type {
  ActivityResponse,
  GuidesResponse,
  IncomeStatementResponse,
  InvoicesResponse,
  ObligationsResponse,
  OverviewResponse,
  SeriesResponse,
  SplitResponse,
} from '@contabil/contracts';
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, ApiError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
});

export type Overview = z.infer<typeof OverviewResponse>;
export type Series = z.infer<typeof SeriesResponse>;
export type Guide = z.infer<typeof GuidesResponse>[number];
export type Invoice = z.infer<typeof InvoicesResponse>[number];
export type Obligation = z.infer<typeof ObligationsResponse>[number];
export type Activity = z.infer<typeof ActivityResponse>[number];
export type Split = z.infer<typeof SplitResponse>;

const q = (period?: string | null) => (period ? `?period=${period}` : '');

export const useOverview = (period: string | null) =>
  useQuery({ queryKey: ['overview', period], queryFn: () => api<Overview>(`/me/overview${q(period)}`), placeholderData: (prev) => prev });
export const useSeries = (scope: 'pj' | 'pf' | undefined) =>
  useQuery({
    queryKey: ['series', scope],
    queryFn: () => api<Series>(`/me/series?months=12${scope ? `&scope=${scope}` : ''}`),
    enabled: Boolean(scope),
  });
export const useGuides = () => useQuery({ queryKey: ['guides'], queryFn: () => api<Guide[]>('/me/guides') });
export const useInvoices = (period: string | null) =>
  useQuery({ queryKey: ['invoices', period], queryFn: () => api<Invoice[]>(`/me/invoices${q(period)}`), enabled: Boolean(period) });
export const useObligations = () => useQuery({ queryKey: ['obligations'], queryFn: () => api<Obligation[]>('/me/obligations') });
export const useActivity = () => useQuery({ queryKey: ['activity'], queryFn: () => api<Activity[]>('/me/activity') });
export const useSplit = (period: string | null) =>
  useQuery({ queryKey: ['split', period], queryFn: () => api<Split>(`/me/split${q(period)}`), enabled: Boolean(period) });

/**
 * PDF da guia por link de curta duração. A aba abre no próprio clique (navegadores bloqueiam
 * window.open depois de uma espera) e recebe o endereço quando a API responde.
 */
export const openGuidePdf = async (id: string) => {
  const w = window.open('about:blank', '_blank');
  try {
    const { url } = await api<{ url: string }>(`/me/guides/${id}/pdf`);
    if (w) {
      w.opener = null;
      w.location.href = url;
    } else location.assign(url);
  } catch (e) {
    w?.close();
    throw e;
  }
};

type Statement = z.infer<typeof IncomeStatementResponse>;
/** Pede o comprovante de renda e espera o worker gerar o PDF (até 1 minuto). */
export function useIncomeStatement() {
  return useMutation({
    mutationFn: async () => {
      let s = await api<Statement>('/me/income-statement', { method: 'POST' });
      for (let i = 0; i < 40 && s.status !== 'pronto' && s.status !== 'erro'; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        s = await api<Statement>(`/me/income-statement/${s.id}`);
      }
      if (s.status !== 'pronto' || !s.url)
        throw new ApiError(500, 'COMPROVANTE', 'Não conseguimos gerar agora. Tente de novo em alguns minutos.');
      return s.url;
    },
  });
}

/** Centavos → reais (os componentes de formatação trabalham em reais). */
export const r = (minor: number) => minor / 100;

/** Eixo Y com 3 intervalos de passo "redondo" (ex.: 12k → 36k, 20k → 60k, como no mock). */
export function niceScale(maxReais: number): { yMax: number; yTicks: number[] } {
  const target = Math.max(1000, maxReais * 1.05) / 3;
  const mag = 10 ** Math.floor(Math.log10(target));
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((m) => m * mag).find((x) => x >= target)!;
  return { yMax: step * 3, yTicks: [0, step, step * 2, step * 3] };
}
